import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import { NavBarItem } from "../models/navbaritem";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContextType, useAuth } from "../auth";
import useSWR from "swr";
import { UserPage } from "../models/UserPage";
import { baseBackendUrl } from "../service/baseService";

// Only shown while at least one small group is active (see GET /api/groups/any-active).
const groupsPage: NavBarItem = {
  label: "Groups",
  route: "/groups",
};

// Always shown last, after the admin-created custom pages.
const aboutUsPage: NavBarItem = {
  label: "About Us",
  route: "/about-us",
};

const staticPages: NavBarItem[] = [
  {
    label: "Streaming",
    route: "/Streaming",
  },
  {
    label: "Upcoming Events",
    route: "/upcoming-events",
  },
];

const adminPages: NavBarItem[] = [
  {
    label: "Manage Announcements",
    route: "/ManageAnnouncements",
  },
  {
    label: "Manage Custom Pages",
    route: "/ManageUserPage",
    roles: ["Admin", "PageEditor"],
  },
  {
    label: "Manage Groups",
    route: "/admin/groups",
    roles: ["Admin", "GroupAdmin"],
  },
  {
    label: "Communication Center",
    route: "/CommunicationCenter",
  },
  {
    label: "Manage Prayer Requests",
    route: "/admin/prayer-requests",
  },
  {
    label: "Manage Episodes",
    route: "/admin/episodes",
  },
  {
    label: "Manage Users",
    route: "/admin/manage-users",
  },
  {
    label: "Email Analytics",
    route: "/admin/email-analytics",
  },
];


function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The bar is only see-through over the dark video hero at the top of Home;
  // everywhere else (and once Home is scrolled) it's solid so the white text stays readable.
  const transparent = location.pathname === "/" && !scrolled;
  const { HasRole, IsAuthenticated, logout, cookies } =
    React.useContext<UserContextType>(useAuth);

  const visibleAdminPages = React.useMemo(() => {
    return adminPages.filter(
      (page) =>
        (page.roles ?? ["Admin"]).some((role) => HasRole && HasRole(role))
    );
  }, [HasRole]);

  const showAdminMenu = visibleAdminPages.length > 0;
  const fetcher = (url: string) => fetch(url).then((r) => r.json());
  const { data, error, isLoading } = useSWR<UserPage[]>(
    baseBackendUrl + `/api/pages`,
    fetcher
  );

  const { data: groupsActive } = useSWR<{ hasActive: boolean }>(
    baseBackendUrl + `/api/groups/any-active`,
    fetcher
  );
  const showGroups = !!groupsActive?.hasActive;

  const pages = React.useMemo(() => {
    const allPages = [...staticPages];

    if (showGroups) {
      allPages.push(groupsPage);
    }

    if (data && !error) {
      data.forEach((page: UserPage) => {
        allPages.push({
          label: page.shortName || page.title,
          route: `/${page.shortName || page.id}`
        });
      });
    }

    allPages.push(aboutUsPage);

    return allPages;
  }, [data, error, showGroups]);

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const [anchorElAdmin, setAnchorElAdmin] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(null);
  };

  const handleOpenAdminMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAdmin(event.currentTarget);
  };

  const handleCloseAdminMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAdmin(null);
  };

  const handleLinkClick = (link: NavBarItem) => {
    navigate(link.route);
    setAnchorElNav(null);
    setAnchorElAdmin(null);
    setAnchorElUser(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const getInitials = () => {
    return (
      cookies &&
      cookies["UserAuth"].firstName[0] + cookies["UserAuth"].lastName[0]
    );
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        boxShadow: transparent ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        background: transparent ? 'rgba(255, 255, 255, 0.1)' : 'rgba(12, 74, 74, 0.95)',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: "none", md: "flex" },
              fontWeight: 700,
              color: "white",
              textDecoration: "none",
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            Church of God Cincinnati
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              sx={{
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: "block", md: "none" },
                '& .MuiPaper-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(8px)',
                }
              }}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.label}
                  onClick={() => handleLinkClick(page)}
                >
                  <Typography textAlign="center">{page.label}</Typography>
                </MenuItem>
              ))}
              {showAdminMenu && (
                <div>
                  <Divider />
                  {visibleAdminPages.map((page) => (
                    <MenuItem
                      key={page.label}
                      onClick={() => handleLinkClick(page)}
                    >
                      <Typography textAlign="center">{page.label}</Typography>
                    </MenuItem>
                  ))}
                </div>
              )}
            </Menu>
          </Box>
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: "flex", md: "none" },
              flexGrow: 1,
              fontWeight: 700,
              color: "white",
              textDecoration: "none",
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            Church of God Cincinnati
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
            {pages.map((page) => (
              <Button
                key={page.label}
                onClick={() => handleLinkClick(page)}
                sx={{
                  my: 2,
                  color: "white",
                  display: "block",
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {page.label}
              </Button>
            ))}
            {showAdminMenu ? (
              <Box sx={{ flexGrow: 0 }}>
                <Tooltip title="Top secret menu where you do admin stuff.">
                  <Button
                    onClick={handleOpenAdminMenu}
                    sx={{
                      my: 2,
                      color: "white",
                      display: "block",
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    Administration
                  </Button>
                </Tooltip>
                <Menu
                  sx={{
                    mt: "45px",
                    '& .MuiPaper-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(8px)',
                    }
                  }}
                  id="menu-appbar"
                  anchorEl={anchorElAdmin}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElAdmin)}
                  onClose={handleCloseAdminMenu}
                >
                  {visibleAdminPages.map((page) => (
                    <MenuItem
                      key={page.label}
                      onClick={() => handleLinkClick(page)}
                    >
                      <Typography textAlign="center">
                        {page.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ) : (
              <div></div>
            )}
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            {IsAuthenticated && IsAuthenticated() ? (
              <Tooltip title="Open settings">
                <IconButton
                  onClick={handleOpenUserMenu}
                  sx={{
                    p: 0,
                    color: 'white',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  }}
                >
                  {getInitials()}
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                onClick={() =>
                  handleLinkClick({ label: "Login", route: "/login" })
                }
                sx={{
                  my: 2,
                  color: "white",
                  display: "block",
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                Log In
              </Button>
            )}
            <Menu
              sx={{
                mt: "45px",
                '& .MuiPaper-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(8px)',
                }
              }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={logout}>
                <Typography textAlign="center">Log Out</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
export default Navbar;
